<template>
  <div class="tender-detail-header">
    <div class="tender-detail-header__slot-content">
      <slot name="agreementResponsible" />
    </div>
    <div class="tender-detail-header__actions">
      <BaseButton
        v-if="showGiveOfferButton"
        :icon="AddDocumentIcon"
        :text="giveOfferButtonText"
        variant="accent"
        size="sm"
        :uppercase="true"
        @click="emit('on-new-offer-click')"
      />

      <BaseButton
        v-if="showMarkAsDoneButton"
        :icon="CheckWhiteIcon"
        text="Marker som utført"
        variant="primary"
        size="sm"
        :uppercase="true"
        @click="emit('on-mark-as-done-click')"
      />

      <BaseButton
        v-if="showRejectPriceRequestButton"
        :icon="CloseIcon"
        text="Avvis forespørsel"
        variant="tertiary"
        size="sm"
        :uppercase="true"
        @click="emit('on-price-request-rejected-click')"
      />

      <BaseButton
        v-if="showCreatePriceRequestButton"
        :icon="PlusIcon"
        :text="createNewOfferButtonText"
        variant="tertiary"
        size="sm"
        :uppercase="true"
        @click="emit('on-create-price-request-click')"
      />

      <BaseButton
        v-if="showMoveToPriceRequestedButton"
        :icon="PlusIcon"
        text="Gjenåpne prisforespørsel"
        variant="tertiary"
        size="sm"
        :uppercase="true"
        @click="emit('on-move-to-price-requested-click')"
      />

      <BaseButton
        v-if="showInterestedButton"
        v-tooltip="interestedButtonProps.tooltip"
        :icon="interestedButtonProps.icon"
        :text="interestedButtonProps.text"
        variant="primary"
        size="sm"
        :disabled="interestedButtonProps.disabled"
        :uppercase="true"
        :is-loading="interestedButtonProps.isLoading"
        @click="interestedButtonProps.onClick"
      >
        <template v-if="interestedButtonProps.isLoading" #icon>
          <span class="spinner" />
        </template>
      </BaseButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import BaseButton from "@/components/Base/BaseButton/BaseButton.vue";
import AddDocumentIcon from "@/assets/img/add-document.svg";
import AddDocumentWhiteIcon from "@/assets/img/add-document-white.svg";
import CheckWhiteIcon from "@/assets/img/check-white.svg";
import CloseIcon from "@/assets/img/close.svg";
import PlusIcon from "@/assets/img/plus.svg";
import { Tender } from "@/stores/tender/types";
import Tooltip from "primevue/tooltip";
import { useTenderApi } from "@/services/api/useTenderApi";
import { useUserStore } from "@/stores/user";

interface Props {
  supplierId: number;
  tender: Tender;
  supplierRequestId: number | null;
  showRejectPriceRequestButton: boolean;
  showMarkAsDoneButton: boolean;
  showGiveOfferButton: boolean;
  showCreatePriceRequestButton: boolean;
  showMoveToPriceRequestedButton: boolean;
  showInterestedButton?: boolean;
  isInterested?: boolean;
  isInterestedLoading?: boolean;
  canSetInterested?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  supplierId: 0,
  tender: () => ({}) as Tender,
  showInterestedButton: false,
  isInterested: false,
  isInterestedLoading: false,
  canSetInterested: false,
});

const emit = defineEmits<{
  (e: "on-new-offer-click"): void;
  (e: "on-create-price-request-click"): void;
  (e: "on-mark-as-done-click"): void;
  (e: "on-price-request-rejected-click"): void;
  (e: "on-move-to-price-requested-click"): void;
  (e: "on-interested-click"): void;
}>();

const { setInterested } = useTenderApi();
const userStore = useUserStore();

// Local loading state for interested button
const isInterestedLoading = ref(false);

// Local state to track if user has indicated interest
const hasLocallyIndicatedInterest = ref(false);

const isEstimateRequest = computed(() => {
  const supplierRequest =
    props.tender?.priceRequests?.[0]?.supplierRequests?.find(
      (req) => req.supplierId === props.supplierId,
    );
  return supplierRequest?.requestEstimates;
});

const giveOfferButtonText = computed(() =>
  isEstimateRequest.value ? "Gi estimat" : "Gi tilbud",
);

const createNewOfferButtonText = computed(() =>
  isEstimateRequest.value ? "Gi nytt estimat" : "Gi nytt tilbud",
);

onMounted(() => {
  console.log("isEstimateRequest", isEstimateRequest.value);
});

const handleInterestedClick = async () => {
  if (!props.supplierRequestId || !userStore.user?.userId) return;

  isInterestedLoading.value = true;
  try {
    const response = await setInterested({
      supplierId: props.supplierId,
      supplierRequestId: props.supplierRequestId,
      data: {
        userId: userStore.user.userId,
        comment: "Leverandøren er interessert",
        reasons: [],
      },
    });
    console.log("response", response);
    if (response) {
      hasLocallyIndicatedInterest.value = true;
      emit("on-interested-click");
    }
  } catch (e) {
    // Optionally handle error
    console.error(e);
  } finally {
    isInterestedLoading.value = false;
  }
};

/**
 * interestedButtonProps: Centralizes all props for the interested button for clarity and maintainability.
 * Adjust logic here if button behavior or appearance needs to change.
 */
const interestedButtonProps = computed(() => {
  const isInterested = props.isInterested || hasLocallyIndicatedInterest.value;
  const text = isInterested ? "Interesert" : "Meld interesse";
  const icon = isInterested ? CheckWhiteIcon : AddDocumentWhiteIcon;
  const tooltip = isInterested
    ? "Du har meldt interesse. For å angre, avvis prisforespørselen."
    : "";
  const disabled =
    isInterested || isInterestedLoading.value || !props.canSetInterested;
  const isLoading = isInterestedLoading.value;
  const onClick = !disabled ? handleInterestedClick : undefined;
  return { text, icon, tooltip, disabled, isLoading, onClick };
});

defineExpose({ directives: { Tooltip } });
</script>

<style scoped lang="scss">
.tender-detail-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 50px;
  background: #fff;
  box-shadow:
    0px -1px 0px 0px #d0d0d0 inset,
    0px 1px 0px 0px #d0d0d0 inset;

  &__slot-content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    text-align: left;
    font-size: 16px;
    font-weight: 600;
    color: #000;
  }

  &__info {
    flex: 1;
    text-align: center;
    font-size: 14px;
    color: #555;
  }

  &__tender-id,
  &__customer,
  &__valid-through {
    margin: 5px 0;
  }

  &__actions {
    flex: 1;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 6px;
  }
}

.spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid #fff;
  border-top: 2px solid #1976d2;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  vertical-align: middle;
}
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}
</style>
